import type { ScheduleOrder } from './filterEvents'

/** A row the merged schedule list orders by. Structural, so this stays
 *  independent of both the game and the event feature's own types. */
interface TimedRow {
  starts_at?: string | null
}

/** One paginated, server-sorted stream's position in the merged list. */
export interface StreamFrontier {
  /** `starts_at` of the furthest loaded row, or null when nothing is loaded. */
  lastStartsAt: string | null
  /** True while the server says more pages remain. */
  hasMore: boolean
  /**
   * True while this stream is loading a list it does not hold yet — a first
   * fetch, or a refetch after the selected period changed.
   *
   * Distinct from `hasMore: false`, which means "this stream is COMPLETE".
   * Without the distinction a stream mid-fetch reads as complete, stops
   * constraining the watermark, and lets the OTHER stream's rows through
   * alone — which is how a period switch briefly merged one period's games
   * with another period's events.
   */
  isPending?: boolean
}

export type Watermark =
  /** Both streams are exhausted — nothing is held back. */
  | { kind: 'open' }
  /** Admit rows at or before this instant (asc), at or after it (desc). */
  | { kind: 'at'; time: number }
  /** A paging stream has loaded nothing usable yet — admit nothing. */
  | { kind: 'closed' }

export interface WatermarkArgs {
  games: StreamFrontier
  events: StreamFrontier
  order?: ScheduleOrder
}

const toTime = (iso?: string | null): number | null => {
  if (!iso) {
    return null
  }
  const time = new Date(iso).getTime()
  return Number.isNaN(time) ? null : time
}

/**
 * The furthest point a stream's loaded pages reach.
 *
 * Derived across every loaded row rather than trusting index 0 or -1: the rows
 * do arrive server-sorted, but a boundary computed from an ASSUMED sort is
 * silently wrong the moment the assumption breaks, and the symptom — a few
 * missing rows — looks nothing like its cause.
 */
export const frontierOf = (
  rows: ReadonlyArray<TimedRow | null | undefined>,
  order: ScheduleOrder = 'asc'
): string | null => {
  const isDesc = order === 'desc'
  let best: number | null = null
  let bestIso: string | null = null

  rows.forEach((row) => {
    const iso = row?.starts_at ?? null
    const time = toTime(iso)
    if (time === null) {
      return
    }
    if (best === null || (isDesc ? time < best : time > best)) {
      best = time
      bestIso = iso
    }
  })

  return bestIso
}

/**
 * How far into the merged list we can render and still be provably complete.
 *
 * Games and events paginate independently, each server-sorted in display order.
 * Neither can ever produce a row earlier than its own furthest loaded one — so
 * everything at or before the NEARER of the two frontiers is guaranteed to be
 * the whole answer for that span. Past it, the other stream may still be
 * holding rows that belong higher up, and rendering them anyway makes the list
 * reshuffle under a user who has already scrolled by.
 *
 * An exhausted stream cannot hold anything back, so its frontier drops out of
 * the calculation entirely. That is why a COMPLETE event list collapses this to
 * the games-only boundary this generalizes — the same rule, with one of the two
 * cursors pinned at infinity.
 */
export const resolveWatermark = ({
  games,
  events,
  order = 'asc',
}: WatermarkArgs): Watermark => {
  // A stream that has not loaded the list it is being asked about cannot be
  // reasoned against at all: it holds either nothing or the PREVIOUS period's
  // rows. Admitting the other stream alone would render one period's games
  // beside another period's events, and the boundary derived from stale rows
  // would withhold exactly the rows that are arriving.
  if (games.isPending || events.isPending) {
    return { kind: 'closed' }
  }

  const bounded: number[] = []

  for (const stream of [games, events]) {
    if (!stream.hasMore) {
      continue
    }
    const time = toTime(stream.lastStartsAt)
    if (time === null) {
      // A first page is still in flight. Admitting the other stream alone would
      // briefly show events above games about to arrive — the reshuffle this
      // whole mechanism exists to prevent.
      return { kind: 'closed' }
    }
    bounded.push(time)
  }

  if (!bounded.length) {
    return { kind: 'open' }
  }

  // Descending, a frontier is the OLDEST row loaded, so the constraining stream
  // is the one that has not reached as far back — the larger value.
  return {
    kind: 'at',
    time: order === 'desc' ? Math.max(...bounded) : Math.min(...bounded),
  }
}

/**
 * The rows a watermark admits, in their original order.
 *
 * `startsAtOf` is passed in rather than read off the row so games and events
 * can share this without a common base type.
 */
export const admitByWatermark = <T>(
  rows: ReadonlyArray<T>,
  startsAtOf: (row: T) => string | null | undefined,
  watermark: Watermark,
  order: ScheduleOrder = 'asc'
): T[] => {
  if (watermark.kind === 'closed') {
    return []
  }

  // Untouched, unparseable dates included — the downstream day-bucketing already
  // drops those, and an exhausted pair of streams has nothing to withhold.
  if (watermark.kind === 'open') {
    return [...rows]
  }

  const isDesc = order === 'desc'
  return rows.filter((row) => {
    const time = toTime(startsAtOf(row))
    if (time === null) {
      return false
    }
    return isDesc ? time >= watermark.time : time <= watermark.time
  })
}

/**
 * Which stream is holding the boundary back — the one to fetch on scroll-end.
 *
 * Advancing the OTHER one would pull a page that changes nothing visible, so
 * the list would sit still while requests went out.
 *
 * A tie goes to games: they are the denser stream, so their next page is far
 * likelier to actually move the watermark than an event page spanning months.
 */
export const laggingStream = (
  args: WatermarkArgs
): 'games' | 'events' | null => {
  const watermark = resolveWatermark(args)

  if (watermark.kind === 'open') {
    return null
  }

  if (watermark.kind === 'closed') {
    // Nothing usable loaded yet — wait on whichever stream is empty, games
    // first when both are.
    return args.games.hasMore && toTime(args.games.lastStartsAt) === null
      ? 'games'
      : 'events'
  }

  const gameTime = args.games.hasMore ? toTime(args.games.lastStartsAt) : null
  return gameTime !== null && gameTime === watermark.time ? 'games' : 'events'
}
